import { useCallback, useEffect, useRef, useState } from 'react';

import { Avatar, Box, Button, CircularProgress, IconButton, InputAdornment, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { alpha, keyframes, styled, useTheme } from '@mui/material/styles';

import useAuth from '../hooks/useAuth';
import axios from '../utils/axios';
import Iconify from './Iconify';

// ---------------------------------------------------------------------
//  Custom ChatPulse (Chatwoot public widget API) chat panel.
//
//  Mirrors lib/utils/chatpulse_api.dart so messages land in the same
//  inbox the support team already monitors.
//
//  Base:  https://app.usechatpulse.com/public/api/v1/inboxes/{token}
//    POST /contacts                                          → create
//    POST /contacts/{src_id}/conversations                   → new convo
//    GET  /contacts/{src_id}/conversations/{cid}/messages    → list
//    POST /contacts/{src_id}/conversations/{cid}/messages    → send
//
//  Persists the created contact in localStorage so refreshes / sessions
//  reuse the same Chatwoot contact (and message history is preserved).
//
//  Polls every POLL_INTERVAL_MS for new agent replies while the panel
//  is open. Stops polling when closed.
//
//  Exposes window.bilalsadaChat.{open,close,toggle} for any code to pop the
//  widget without importing.
// ---------------------------------------------------------------------

const BASE_URL = 'https://app.usechatpulse.com';
// Bilalsadasub's real ChatPulse inbox — the SAME one the Flutter app uses
// (chatpulse.dart chatPulseInboxToken) and the backend now creates contacts in
// (ChatPulseController@getOrCreateContact). All three must match or the
// backend-returned source_id won't exist here and conversations 404.
const INBOX_TOKEN = 'uSPxAwaAdAZJ7husntXVBuNL';
// Cache key is per-user so different users on the same browser don't
// see each other's chat history. `anon` is used for guests (login,
// signup, forgot-password). Prefix bumps invalidate stale entries
// from older builds (v1 single-key, v2 anon-leak under user keys).
// v6 bump: inbox corrected back to Bilal's real inbox (uSPx…) — the bpay inbox
// (KGUk…) from the v5 fix was wrong. Invalidate any contact cached against it.
const STORAGE_KEY_PREFIX = 'bilalsada_chatpulse_contact_v6_';
const POLL_INTERVAL_MS = 5000;

const API = `${BASE_URL}/public/api/v1/inboxes/${INBOX_TOKEN}`;

// ChatPulse "Identifier Validation" HMAC key (mirrors chatpulse.dart). Without
// identifier_hash the inbox rejects the identifier when validation is enabled.
const IDENTIFIER_KEY = 's38pLAbZj37vLK8fsjywm5Di';
async function hmacHex(msg) {
  try {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey('raw', enc.encode(IDENTIFIER_KEY), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const sig = await crypto.subtle.sign('HMAC', key, enc.encode(msg));
    return Array.from(new Uint8Array(sig)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return null; }
}
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

// ---------------------------------------------------------------------

// Frontend doesn't see the real user_id (the backend's generateDetails
// deliberately omits it). The accessToken IS our identity — different
// tokens → different cache slots, no token → anonymous slot.
function readAccessToken() {
  if (typeof window === 'undefined') return null;
  const tk = window.localStorage.getItem('accessToken');
  if (!tk || tk === 'undefined' || tk === 'null') return null;
  return tk;
}

function identityKey() {
  const tk = readAccessToken();
  // Last 12 chars are enough to distinguish sessions without bloating
  // the storage key and without leaking the full token into one.
  return tk ? `t_${tk.slice(-12)}` : 'anon';
}

function storageKey(identity) {
  return STORAGE_KEY_PREFIX + (identity || identityKey());
}

function loadContact(identity) {
  try {
    const raw = window.localStorage.getItem(storageKey(identity));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveContact(identity, c) {
  try { window.localStorage.setItem(storageKey(identity), JSON.stringify(c)); } catch { /* quota */ }
}

function clearContact(identity) {
  try { window.localStorage.removeItem(storageKey(identity)); } catch { /* */ }
}

// 08012345678 / 2348012345678 / +2348012345678 → +2348012345678
function toE164Nigeria(raw) {
  if (!raw) return null;
  const cleaned = String(raw).replace(/[^\d+]/g, '');
  if (!cleaned) return null;
  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1);
    return digits.length >= 10 && digits.length <= 15 ? cleaned : null;
  }
  if (cleaned.length === 11 && cleaned.startsWith('0')) return `+234${cleaned.slice(1)}`;
  if (cleaned.length === 13 && cleaned.startsWith('234')) return `+${cleaned}`;
  if (cleaned.length === 10) return `+234${cleaned}`;
  return null;
}

// ---------------------------------------------------------------------

// Authed users go through the app backend, which keeps a single
// Chatwoot contact per user_id. That way the same source_id and
// pubsub_token come back on every device/browser — chat history
// follows the user, not the cookie jar.
async function ensureContactViaBackend(accessToken) {
  const res = await axios.get(`/api/me/chatpulse/contact/${accessToken}`);
  const data = res?.data;
  if (!data?.source_id || !data?.pubsub_token) throw new Error('chatpulse:empty');
  return {
    source_id: String(data.source_id),
    pubsub_token: String(data.pubsub_token),
    contact_id: data.contact_id ? Number(data.contact_id) : undefined,
  };
}

async function ensureContact(user) {
  const accessToken = readAccessToken();
  const isAuthed = !!accessToken;
  const identity = identityKey();

  // Authed path — backend is the source of truth. The token decodes
  // server-side, so we get a properly-named Chatwoot contact instead
  // of an anonymous "aged-field-947".
  if (isAuthed) {
    const cached = loadContact(identity);
    if (cached?.source_id) return cached;
    try {
      const contact = await ensureContactViaBackend(accessToken);
      saveContact(identity, contact);
      return contact;
    } catch (err) {
      console.debug('[chatpulse] backend ensure failed, falling back to anon POST', err?.message || err);
    }
  }

  // Anonymous path (guests on login/signup/forgot-password, or fallback).
  const anonCached = loadContact('anon');
  if (anonCached?.source_id) return anonCached;

  const body = {};
  const name = user?.name || user?.username;
  if (name) body.name = name;
  if (user?.email) body.email = user.email;
  const phone = toE164Nigeria(user?.phone);
  if (phone) body.phone_number = phone;
  if (user?.username) body.identifier = `bilalsada:${user.username}`;
  else if (user?.id != null) body.identifier = `bilalsada:id:${user.id}`;
  if (body.identifier) {
    const idHash = await hmacHex(body.identifier);
    if (idHash) body.identifier_hash = idHash;
  }

  const res = await fetch(`${API}/contacts`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`contact:${res.status}`);
  const data = await res.json();
  const contact = {
    source_id: String(data.source_id),
    pubsub_token: String(data.pubsub_token),
    contact_id: Number(data.id),
  };
  // Anonymous fallback path: this only runs when isAuthed was false OR
  // the backend call failed, so the right cache slot is the anon slot.
  saveContact('anon', contact);
  return contact;
}

async function ensureConversation(contact, identity) {
  const url = `${API}/contacts/${encodeURIComponent(contact.source_id)}/conversations`;
  try {
    const res = await fetch(url, { headers: JSON_HEADERS });
    if (res.ok) {
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data?.payload || data?.data || []);
      // Reuse the most-recent OPEN / PENDING conversation. Anything
      // RESOLVED (or SNOOZED) is a closed ticket — opening the chat again
      // should start a fresh thread instead of reopening the old one.
      const active = list.filter((c) => {
        const s = String(c?.status ?? '').toLowerCase();
        return s === 'open' || s === 'pending' || s === '';
      });
      if (active.length > 0) {
        // Newest first by created_at if present, else fall back to id.
        active.sort((a, b) => Number(b?.created_at ?? b?.id ?? 0) - Number(a?.created_at ?? a?.id ?? 0));
        return Number(active[0].id);
      }
    }
  } catch { /* fall through to create */ }
  const create = await fetch(url, { method: 'POST', headers: JSON_HEADERS, body: '{}' });
  if (!create.ok) {
    // Stale source_id (inbox token rotated). Wipe + bubble up so caller can retry.
    if (create.status === 404) clearContact(identity);
    throw new Error(`conversation:${create.status}`);
  }
  const data = await create.json();
  return Number(data.id);
}

function normalizeMessage(m) {
  const rawAttachments = Array.isArray(m.attachments) ? m.attachments : [];
  return {
    id: Number(m.id),
    content: String(m.content ?? ''),
    fromUser: Number(m.message_type ?? 0) === 0,
    createdAt: m.created_at ? new Date(Number(m.created_at) * 1000) : new Date(),
    senderName: m.sender?.name,
    senderAvatar: m.sender?.thumbnail || m.sender?.avatar_url,
    conversationId: m.conversation_id ?? m.conversation?.id,
    attachments: rawAttachments.map((a) => ({
      id: a?.id,
      url: a?.data_url || a?.file_url || a?.url,
      thumb: a?.thumb_url || a?.data_url || a?.file_url,
      fileType: a?.file_type || a?.mime || '',
      name: a?.file_name || (typeof a?.data_url === 'string' ? a.data_url.split('/').pop() : 'attachment'),
      size: a?.file_size || 0,
    })).filter((a) => a.url),
  };
}

async function listMessages(contact, conversationId, before) {
  const base = `${API}/contacts/${encodeURIComponent(contact.source_id)}/conversations/${conversationId}/messages`;
  const url = before ? `${base}?before=${before}` : base;
  const res = await fetch(url, { headers: JSON_HEADERS });
  if (!res.ok) throw new Error(`messages:${res.status}`);
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data?.payload || data?.data || []);
  return list
    .filter((m) => Number(m.message_type ?? 0) !== 2) // skip template
    .map(normalizeMessage)
    .sort((a, b) => a.createdAt - b.createdAt);
}

// Stitch messages from EVERY conversation the contact has into one
// continuous time-ordered timeline. Used for the panel's message list —
// Chatwoot tickets ("conversations") are an internal concept, but from
// the user's perspective there is just one ongoing chat with support.
async function listAllMessages(contact, before) {
  // Stitches messages from every conversation. With `before` we ask
  // each conversation for the page of messages older than that id —
  // the IDs are globally monotonic so this returns the next page worth
  // of older history merged across conversations.
  const convsUrl = `${API}/contacts/${encodeURIComponent(contact.source_id)}/conversations`;
  const res = await fetch(convsUrl, { headers: JSON_HEADERS });
  if (!res.ok) throw new Error(`conversations:${res.status}`);
  const data = await res.json();
  const conversations = Array.isArray(data) ? data : (data?.payload || data?.data || []);
  const lists = await Promise.all(
    conversations.map(async (c) => {
      const cid = Number(c?.id ?? 0);
      if (!cid) return [];
      try { return await listMessages(contact, cid, before); } catch { return []; }
    })
  );
  return lists.flat().sort((a, b) => a.createdAt - b.createdAt);
}

// Scan every conversation the contact has — not just the "active" one.
// Returns the newest agent message across all of them whose id is greater
// than `since`, or null. This catches replies the agent sent to a
// resolved / older conversation, which the single-conversation poll
// would otherwise miss.
async function findNewestAgentMessageAcrossAllConversations(contact, since) {
  const url = `${API}/contacts/${encodeURIComponent(contact.source_id)}/conversations`;
  const res = await fetch(url, { headers: JSON_HEADERS });
  if (!res.ok) return { newest: null, allSeen: since };
  const data = await res.json();
  const conversations = Array.isArray(data) ? data : (data?.payload || data?.data || []);
  let newest = null;
  let allSeen = since;
  for (const c of conversations) { // eslint-disable-line no-restricted-syntax
    const cid = Number(c?.id ?? 0);
    if (!cid) continue; // eslint-disable-line no-continue
    let msgs;
    try {
      // eslint-disable-next-line no-await-in-loop
      msgs = await listMessages(contact, cid);
    } catch { continue; } // eslint-disable-line no-continue
    for (const m of msgs) { // eslint-disable-line no-restricted-syntax
      if (m.id > allSeen) allSeen = m.id;
      if (!m.fromUser && m.id > since && (!newest || m.id > newest.id)) {
        newest = { ...m, conversationId: cid };
      }
    }
  }
  return { newest, allSeen };
}

// ---------------------------------------------------------------------
//  Sound — short two-note chime via Web Audio. No asset needed.
// ---------------------------------------------------------------------

let sharedAudioCtx = null;
function playChime() {
  try {
    if (typeof window === 'undefined') return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!sharedAudioCtx) sharedAudioCtx = new Ctx();
    if (sharedAudioCtx.state === 'suspended') sharedAudioCtx.resume();
    const ctx = sharedAudioCtx;
    const now = ctx.currentTime;
    const tone = (freq, startOffset, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + startOffset);
      gain.gain.exponentialRampToValueAtTime(0.18, now + startOffset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + startOffset);
      osc.stop(now + startOffset + duration + 0.02);
    };
    tone(880, 0, 0.18);
    tone(1320, 0.10, 0.22);
  } catch { /* silent — autoplay block etc. */ }
}

// ---------------------------------------------------------------------
//  OS browser notification (when tab is hidden + permission granted)
// ---------------------------------------------------------------------

function maybeRequestNotifPermission() {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    try { Notification.requestPermission().catch(() => {}); } catch { /* */ }
  }
}

function fireBrowserNotification(msg, onClick) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  // Only fire when the tab isn't already in front — otherwise the in-app
  // peek bubble is enough.
  if (!document.hidden) return;
  try {
    const body = msg.content && msg.content.length > 100 ? `${msg.content.slice(0, 100)}…` : (msg.content || 'New message');
    const n = new Notification(msg.senderName || 'Bilalsadasub Support', {
      body,
      icon: '/logo_icon.webp',
      tag: 'bilalsada-chat',
      renotify: true,
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* */ }
      onClick?.();
      n.close();
    };
  } catch { /* */ }
}

// ---------------------------------------------------------------------
//  Document title flash — alternates "(N) New message — Bilalsadasub" with the
//  original title so the tab itself signals unread.
// ---------------------------------------------------------------------

const titleFlash = {
  timer: null,
  originalTitle: null,
  start(count) {
    if (typeof document === 'undefined') return;
    if (this.originalTitle == null) this.originalTitle = document.title;
    if (this.timer) clearInterval(this.timer);
    let on = true;
    const flashLabel = `(${count}) New message — Bilalsadasub`;
    document.title = flashLabel;
    this.timer = setInterval(() => {
      on = !on;
      document.title = on ? flashLabel : (this.originalTitle || 'Bilalsadasub');
    }, 1500);
  },
  stop() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this.originalTitle != null && typeof document !== 'undefined') {
      document.title = this.originalTitle;
    }
  },
};

// ---------------------------------------------------------------------
//  WebSocket — Chatwoot ActionCable. The contact's pubsub_token is the
//  channel key. As soon as an agent posts a reply in any of the
//  contact's conversations, a `message.created` frame arrives in well
//  under a second. Polling stays in place as a fallback for when the
//  socket is down (mobile suspend, flaky network, etc).
// ---------------------------------------------------------------------

function makeChatPulseSocket(pubsubToken, onAgentMessage) {
  const base = BASE_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
  const url = `${base}/cable`;
  const identifier = JSON.stringify({ channel: 'RoomChannel', pubsub_token: pubsubToken });
  let ws = null;
  let attempt = 0;
  let reconnectTimer = null;
  let disposed = false;

  const schedule = () => {
    if (disposed) return;
    attempt += 1;
    const delaySec = attempt < 6 ? attempt * 2 : 12;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connect, delaySec * 1000); // eslint-disable-line no-use-before-define
  };

  function connect() {
    if (disposed) return;
    try {
      ws = new WebSocket(url);
    } catch {
      schedule();
      return;
    }
    ws.onopen = () => {
      try { ws.send(JSON.stringify({ command: 'subscribe', identifier })); } catch { /* */ }
    };
    ws.onmessage = (ev) => {
      try {
        const parsed = JSON.parse(ev.data);
        if (!parsed || typeof parsed !== 'object') return;
        if (parsed.type === 'ping' || parsed.type === 'welcome') return;
        if (parsed.type === 'confirm_subscription') {
          attempt = 0;
          console.debug('[chatpulse] ws subscribed');
          return;
        }
        const event = parsed.message?.event;
        const data = parsed.message?.data;
        if (event === 'message.created' && data && Number(data.message_type ?? 0) !== 0) {
          onAgentMessage(normalizeMessage(data));
        }
      } catch { /* */ }
    };
    ws.onerror = () => { /* let onclose schedule reconnect */ };
    ws.onclose = () => { if (!disposed) schedule(); };
  }

  connect();

  return () => {
    disposed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (ws) { try { ws.close(); } catch { /* */ } }
  };
}

async function sendMessage(contact, conversationId, content) {
  const url = `${API}/contacts/${encodeURIComponent(contact.source_id)}/conversations/${conversationId}/messages`;
  const res = await fetch(url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ content }),
  });
  if (!res.ok) throw new Error(`send:${res.status}`);
  return res.json();
}

// Chatwoot accepts multipart for attachments. Browser sets the correct
// multipart boundary automatically when we pass a FormData body.
async function sendAttachment(contact, conversationId, file, content) {
  const url = `${API}/contacts/${encodeURIComponent(contact.source_id)}/conversations/${conversationId}/messages`;
  const fd = new FormData();
  if (content) fd.append('content', content);
  fd.append('attachments[]', file, file.name);
  const res = await fetch(url, { method: 'POST', body: fd });
  if (!res.ok) throw new Error(`send_attachment:${res.status}`);
  return res.json();
}

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5 MB cap per file

function isImageMime(t = '') {
  return /^image\//i.test(String(t));
}

function humanFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// =====================================================================
//  UI
// =====================================================================

const FloatBtn = styled('button')(({ theme }) => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1500,
  width: 62,
  height: 62,
  borderRadius: '50%',
  border: `1px solid ${alpha(theme.palette.primary.dark, 0.45)}`,
  cursor: 'pointer',
  background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  display: 'grid',
  placeItems: 'center',
  animation: `${glowPulse} 3.2s ease-in-out infinite`,
  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
  '&:hover': {
    transform: 'translateY(-2px) scale(1.04)',
    animation: 'none',
    boxShadow: `0 20px 44px -8px ${alpha(theme.palette.primary.main, 0.85)}`,
  },
  '&:active': { transform: 'scale(0.96)' },
  [theme.breakpoints.down('sm')]: { bottom: 18, right: 18, width: 56, height: 56 },
}));

// Speech-bubble preview slot — sits to the LEFT of the FAB. On mobile the
// FAB is smaller so we shift accordingly.
const peekIn = keyframes({
  '0%':   { opacity: 0, transform: 'translateY(8px) scale(0.96)' },
  '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
});

// Green "online" heartbeat ring behind the support avatar's status dot.
const pulseRing = keyframes({
  '0%':   { boxShadow: '0 0 0 0 rgba(34,197,94,0.55)' },
  '70%':  { boxShadow: '0 0 0 6px rgba(34,197,94,0)' },
  '100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0)' },
});

// Slow gold glow that breathes on the floating launcher to draw the eye.
const glowPulse = keyframes({
  '0%,100%': { boxShadow: '0 12px 30px -8px rgba(248,199,54,0.55)' },
  '50%':     { boxShadow: '0 16px 40px -6px rgba(248,199,54,0.85)' },
});

const PeekWrap = styled('div')(({ theme }) => ({
  position: 'fixed',
  bottom: 28,
  right: 96,
  zIndex: 1500,
  maxWidth: 320,
  width: 'calc(100vw - 120px)',
  animation: `${peekIn} 0.22s ease-out both`,
  cursor: 'pointer',
  [theme.breakpoints.down('sm')]: { right: 80, bottom: 22, maxWidth: 260 },
}));

const PeekCard = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1.5, 1.75),
  paddingRight: theme.spacing(4.5),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 16,
  boxShadow: `0 18px 40px -16px ${alpha(theme.palette.common.black, 0.3)}`,
  // Tail pointing right toward the FAB.
  '&::after': {
    content: '""',
    position: 'absolute',
    right: -7,
    bottom: 16,
    width: 14, height: 14,
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    transform: 'rotate(45deg)',
  },
}));

const Panel = styled('div')(({ theme }) => ({
  position: 'fixed',
  bottom: 100,
  right: 24,
  zIndex: 1500,
  width: 380,
  height: 560,
  maxHeight: 'calc(100vh - 120px)',
  borderRadius: 20,
  overflow: 'hidden',
  background: theme.palette.background.paper,
  boxShadow: `0 24px 60px -16px ${alpha(theme.palette.common.black, 0.35)}`,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    bottom: 0, right: 0, left: 0,
    width: '100%',
    height: '100dvh',
    maxHeight: '100dvh',
    borderRadius: 0,
  },
}));

// ---------------------------------------------------------------------

export default function ChatPulseWidget() {
  const theme = useTheme();
  const { user, isInitialized } = useAuth();
  const lastUserIdRef = useRef(undefined);
  // Loud version marker — if the page is on stale JS this line is missing.
  // eslint-disable-next-line no-console
  console.log('[chatpulse] widget v4', { isInitialized, identity: identityKey() });

  const [open, setOpen] = useState(false);
  // Authed users: use whatever's cached against the *current* token slot.
  // Guests: use the anon slot. Never let an anon contact leak into an
  // authed session and vice versa.
  const [contact, setContact] = useState(() => loadContact(identityKey()));
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  // True once we've done a server round-trip — used to decide between
  // shimmer (not loaded yet) and the empty state (loaded, no messages).
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);
  const [unread, setUnread] = useState(0);
  // Speech-bubble preview that pops next to the FAB when an agent message
  // arrives and the panel is closed. Holds the message itself so we can
  // render sender + content.
  const [peek, setPeek] = useState(null);
  // Reverse-pagination state. When the user scrolls within ~80 px of the
  // top, we fetch the next page of older messages.
  const [loadingOlder, setLoadingOlder] = useState(false);
  const olderExhaustedRef = useRef(false);
  const scrollPreserveRef = useRef(null);
  const prependingRef = useRef(false);
  // Pending file attachment (chosen via paperclip or drag/drop) before
  // the user hits send. Cleared once the message lands.
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const pollRef = useRef(null);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastSeenIdRef = useRef(0);
  const peekTimerRef = useRef(null);
  // Latest agent-message handler. Held in a ref so the WebSocket effect
  // can call current behaviour without re-subscribing whenever `open` or
  // `conversationId` change.
  const agentHandlerRef = useRef(null);

  // Expose tiny global so any UI can pop the chat.
  useEffect(() => {
    window.bilalsadaChat = {
      open:   () => setOpen(true),
      close:  () => setOpen(false),
      toggle: () => setOpen((v) => !v),
    };
  }, []);

  // On mount, if we already have a cached contact (returning user), silently
  // resolve their existing conversation + pre-fill messages so when they
  // open the panel there's no shimmer flash — history is already there.
  // Also primes the unread badge so it can fire before the panel opens.
  //
  // Retries with backoff so a transient network blip on first load doesn't
  // leave the widget permanently silent.
  useEffect(() => {
    const currentIdentity = identityKey();
    // eslint-disable-next-line no-console
    console.log('[chatpulse] mount effect', { isInitialized, identity: currentIdentity, hasContact: !!contact, conversationId });

    // Wait for JWT context to finish loading before creating any contact —
    // otherwise we'd register an anonymous one for a user who's about to
    // resolve, and the bot would greet them with Chatwoot's random name
    // (e.g. "aged-field-947") instead of theirs.
    if (!isInitialized) return undefined;

    const prevIdentity = lastUserIdRef.current;
    const identityChanged = prevIdentity !== undefined && prevIdentity !== currentIdentity;
    lastUserIdRef.current = currentIdentity;

    // Login / logout transition — drop the previous contact + thread so
    // the next fetch goes through the app backend for the new identity.
    if (identityChanged) {
      setContact(null);
      setConversationId(null);
      setMessages([]);
      setLoaded(false);
      lastSeenIdRef.current = 0;
      olderExhaustedRef.current = false;
    } else if (conversationId) {
      return undefined;
    }

    // setContact above is async; this render's `contact` is still the
    // stale value from before the transition. Compute the local starting
    // contact explicitly so tryOnce() doesn't short-circuit to the old
    // anon contact when we WANT to fetch the new authed one.
    const startingContact = identityChanged ? null : contact;

    let alive = true;
    let attempt = 0;
    let retryTimer = null;
    const tryOnce = async () => {
      attempt += 1;
      try {
        const c = startingContact ?? await ensureContact(user);
        if (!alive) return;
        if (!startingContact) setContact(c);
        const cid = await ensureConversation(c, currentIdentity);
        if (!alive) return;
        setConversationId(cid);
        const msgs = await listAllMessages(c);
        if (!alive) return;
        setMessages(msgs);
        setLoaded(true);
        lastSeenIdRef.current = msgs.length ? msgs[msgs.length - 1].id : 0;
        // eslint-disable-next-line no-console
        console.log('[chatpulse] mount fetch ok', { cid, count: msgs.length, lastSeen: lastSeenIdRef.current });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('[chatpulse] mount fetch failed', { attempt, err: String(err?.message || err) });
        if (!alive) return;
        // Back off: 5s, 15s, 30s, then every 60s.
        const delays = [5000, 15000, 30000];
        const delay = delays[attempt - 1] ?? 60000;
        retryTimer = setTimeout(tryOnce, delay);
      }
    };
    tryOnce();
    return () => { alive = false; if (retryTimer) clearTimeout(retryTimer); };
    // Watch `user` so login/logout transitions re-trigger the effect.
    // The identityKey() call inside reads the freshly-saved accessToken
    // so we drop the previous (anon or other) contact and pull the new
    // user's Chatwoot contact via the backend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, user]);

  const bootstrap = useCallback(async () => {
    setError(null);
    try {
      const c = contact ?? await ensureContact(user);
      if (!contact) setContact(c);
      const cid = await ensureConversation(c, identityKey());
      setConversationId(cid);
      const msgs = await listAllMessages(c);
      setMessages(msgs);
      setLoaded(true);
      lastSeenIdRef.current = msgs.length ? msgs[msgs.length - 1].id : 0;
      setUnread(0);
    } catch (err) {
      // If contact was stale we cleared it — retry once with a fresh one.
      if (String(err.message).startsWith('conversation:404')) {
        try {
          const fresh = await ensureContact(user);
          setContact(fresh);
          const cid = await ensureConversation(fresh, user?.id);
          setConversationId(cid);
          const msgs = await listAllMessages(fresh);
          setMessages(msgs);
          setLoaded(true);
          lastSeenIdRef.current = msgs.length ? msgs[msgs.length - 1].id : 0;
          setError(null);
          return;
        } catch (e2) {
          setError(e2?.message || 'Could not connect to support');
        }
      } else {
        setError(err?.message || 'Could not connect to support');
      }
    }
  }, [contact, user]);

  // Open panel → ensure contact + conversation + messages.
  // Wait for JWT init so we don't accidentally create an anon Chatwoot
  // contact for a user whose session is still resolving.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line no-console
    console.log('[chatpulse] open effect', { isInitialized, identity: identityKey(), conversationId, hasContact: !!contact });
    if (!isInitialized) return;
    if (!conversationId) bootstrap();
  }, [open, conversationId, bootstrap, isInitialized, contact]);

  // Poll for new agent messages while open. Stitches every conversation
  // so the user sees one continuous thread even when old tickets get
  // resolved and new ones are created.
  useEffect(() => {
    if (!open || !contact) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await listAllMessages(contact);
        setMessages((prev) => {
          // Only update if there are new messages — avoids redundant renders.
          if (fresh.length === prev.length && fresh.at(-1)?.id === prev.at(-1)?.id) return prev;
          return fresh;
        });
        lastSeenIdRef.current = fresh.length ? fresh[fresh.length - 1].id : 0;
      } catch { /* swallow */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [open, contact]);

  // Single source of truth for "an agent message arrived". Used by both
  // the WebSocket subscription (instant) and the polling fallback (every
  // 10s). Dedupes via lastSeenIdRef so the same message can't fire twice.
  agentHandlerRef.current = (msg) => {
    if (!msg || msg.id <= lastSeenIdRef.current) return;
    lastSeenIdRef.current = msg.id;
    // If the panel is open, merge directly into the stitched timeline —
    // no badge, no peek, no sound. We accept agent replies from ANY
    // conversation since the user sees one continuous thread.
    if (open) {
      setMessages((prev) => {
        if (prev.some((p) => p.id === msg.id)) return prev;
        return [...prev, msg].sort((a, b) => a.createdAt - b.createdAt);
      });
      return;
    }
    // Otherwise: fire the full notification set.
    setUnread((u) => {
      const next = u + 1;
      titleFlash.start(next);
      return next;
    });
    setPeek(msg);
    playChime();
    fireBrowserNotification(msg, () => setOpen(true));
  };

  // Real-time channel — Chatwoot ActionCable over the contact's
  // pubsub_token. Stays open across panel open/close; only tears down
  // if the contact itself changes (logout / contact rotation).
  useEffect(() => {
    if (!contact?.pubsub_token) return undefined;
    console.debug('[chatpulse] ws connecting');
    const dispose = makeChatPulseSocket(contact.pubsub_token, (msg) => {
      console.debug('[chatpulse] ws msg', { id: msg.id, conv: msg.conversationId });
      agentHandlerRef.current?.(msg);
    });
    return () => { dispose(); };
  }, [contact?.pubsub_token]);

  // Polling fallback — catches anything the socket might have dropped
  // (suspended laptop, momentary disconnect, etc). Runs at half the
  // socket's cadence and goes silent the moment a poll tick finds
  // nothing new.
  useEffect(() => {
    if (!contact) return undefined;
    const id = setInterval(async () => {
      try {
        const { newest, allSeen } = await findNewestAgentMessageAcrossAllConversations(contact, lastSeenIdRef.current);
        if (newest) {
          agentHandlerRef.current?.(newest);
        } else if (allSeen > lastSeenIdRef.current) {
          lastSeenIdRef.current = allSeen;
        }
      } catch { /* */ }
    }, POLL_INTERVAL_MS * 6);
    return () => clearInterval(id);
  }, [contact]);

  // Auto-dismiss the peek bubble after 12s.
  useEffect(() => {
    if (!peek) return undefined;
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => setPeek(null), 12000);
    return () => { if (peekTimerRef.current) clearTimeout(peekTimerRef.current); };
  }, [peek]);

  // Clear the peek bubble the moment the panel opens.
  useEffect(() => {
    if (open && peek) setPeek(null);
  }, [open, peek]);

  // Auto-scroll to the latest message when new ones arrive, OR preserve
  // scroll position when older messages were just prepended via reverse-
  // pagination. Prepending shifts the content down, so we offset
  // scrollTop by the height delta to keep the user's view anchored.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    if (prependingRef.current && scrollPreserveRef.current) {
      const { prevScrollHeight, prevScrollTop } = scrollPreserveRef.current;
      el.scrollTop = el.scrollHeight - prevScrollHeight + prevScrollTop;
      prependingRef.current = false;
      scrollPreserveRef.current = null;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // Fetch the next page of older messages and prepend them. Triggered
  // when the user scrolls within ~80 px of the top of the message list.
  const loadOlder = useCallback(async () => {
    if (loadingOlder || olderExhaustedRef.current || !contact) return;
    if (!messages.length) return;
    const earliestId = Math.min(...messages.map((m) => m.id).filter((id) => id > 0));
    if (!Number.isFinite(earliestId) || earliestId <= 0) return;
    setLoadingOlder(true);
    // Capture the scroll geometry before we mutate state so the next
    // render's effect can compensate.
    if (listRef.current) {
      scrollPreserveRef.current = {
        prevScrollHeight: listRef.current.scrollHeight,
        prevScrollTop: listRef.current.scrollTop,
      };
    }
    try {
      const older = await listAllMessages(contact, earliestId);
      const existing = new Set(messages.map((m) => m.id));
      const fresh = older.filter((m) => !existing.has(m.id) && m.id < earliestId);
      if (!fresh.length) {
        olderExhaustedRef.current = true;
      } else {
        prependingRef.current = true;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const merged = [...fresh.filter((m) => !ids.has(m.id)), ...prev];
          return merged.sort((a, b) => a.createdAt - b.createdAt);
        });
      }
    } catch {
      // Network blip — let the user retry by scrolling.
    } finally {
      setLoadingOlder(false);
    }
  }, [contact, loadingOlder, messages]);

  const handleListScroll = useCallback((e) => {
    if (e?.currentTarget && e.currentTarget.scrollTop < 80) loadOlder();
  }, [loadOlder]);

  // Clear unread badge + stop title flash when opening.
  useEffect(() => {
    if (open) {
      setUnread(0);
      titleFlash.stop();
      lastSeenIdRef.current = messages.length ? messages[messages.length - 1].id : 0;
    }
  }, [open, messages]);

  // Stop title flash when component unmounts (defensive — also restores
  // the original title if the user navigates away mid-flash).
  useEffect(() => () => titleFlash.stop(), []);

  // Open the hidden file picker.
  const pickFile = () => { fileInputRef.current?.click(); };

  const onFileChosen = (e) => {
    const file = e.target?.files?.[0];
    // Always clear the input so picking the same file twice re-fires onChange.
    if (e.target) e.target.value = '';
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError(`Attachment too big — ${humanFileSize(file.size)}. Max is 5 MB.`);
      return;
    }
    setError(null);
    setAttachment(file);
    if (isImageMime(file.type)) {
      const reader = new FileReader();
      reader.onload = () => setAttachmentPreview(String(reader.result || ''));
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const clearAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if ((!text && !attachment) || !contact || !conversationId) return;
    const sentText = text;
    const sentFile = attachment;
    const sentPreview = attachmentPreview;
    setDraft('');
    setAttachment(null);
    setAttachmentPreview(null);
    // Optimistic placeholder so the bubble appears immediately.
    const tempId = -Date.now();
    const optimistic = {
      id: tempId,
      content: sentText,
      fromUser: true,
      createdAt: new Date(),
      sending: true,
      attachments: sentFile ? [{
        id: `tmp-${tempId}`,
        url: sentPreview || '',
        thumb: sentPreview || '',
        fileType: sentFile.type,
        name: sentFile.name,
        size: sentFile.size,
      }] : [],
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      if (sentFile) {
        await sendAttachment(contact, conversationId, sentFile, sentText);
      } else {
        await sendMessage(contact, conversationId, sentText);
      }
      const fresh = await listAllMessages(contact);
      setMessages(fresh);
      lastSeenIdRef.current = fresh.length ? fresh[fresh.length - 1].id : 0;
    } catch (err) {
      setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, sending: false, failed: true } : m)));
      setError(err?.message || 'Could not send. Please try again.');
    }
  };

  return (
    <>
      {/* Peek preview — pops out of the FAB on new agent messages */}
      {peek && !open && (
        <PeekWrap onClick={() => setOpen(true)} role="button" aria-label="Open new message">
          <PeekCard>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setPeek(null); }}
              aria-label="Dismiss preview"
              sx={{ position: 'absolute', top: 4, right: 4, p: 0.5, color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
            >
              <Iconify icon="eva:close-fill" width={14} />
            </IconButton>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Avatar
                src={peek.senderAvatar || undefined}
                alt={peek.senderName || 'Agent'}
                sx={{ width: 32, height: 32, fontSize: '0.78rem', fontWeight: 800, flexShrink: 0 }}
              >
                {(peek.senderName || 'A').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', lineHeight: 1.2 }}>
                  {peek.senderName || 'Bilalsadasub Support'}
                </Typography>
                <Typography sx={{
                  mt: 0.25,
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                  lineHeight: 1.4,
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}>
                  {peek.content || 'New message'}
                </Typography>
              </Box>
            </Stack>
          </PeekCard>
        </PeekWrap>
      )}

      {/* Floating launcher */}
      <FloatBtn
        type="button"
        aria-label={open ? 'Close live chat' : 'Open live chat'}
        onClick={() => {
          // First user gesture is the only time browsers will accept the
          // notification permission prompt — piggyback on the chat-open
          // click so we have one ready next time a message arrives.
          maybeRequestNotifPermission();
          setOpen((v) => !v);
        }}
      >
        <Iconify icon={open ? 'eva:close-fill' : 'eva:message-circle-fill'} width={26} />
        {!open && unread > 0 && (
          <Box sx={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 20, height: 20, px: 0.5,
            borderRadius: '999px',
            bgcolor: '#ef4444', color: '#fff',
            fontWeight: 800, fontSize: '0.7rem',
            display: 'grid', placeItems: 'center',
            border: '2px solid #fff',
          }}>
            {unread > 9 ? '9+' : unread}
          </Box>
        )}
      </FloatBtn>

      {open && (
        <Panel role="dialog" aria-label="Live chat with Bilalsadasub support">
          {/* Header */}
          <Box sx={{
            p: 2,
            pb: 1.75,
            color: '#fff',
            background: 'linear-gradient(135deg, #1e1e1e 0%, #131313 100%)',
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* soft gold glow in the corner */}
            <Box sx={{
              position: 'absolute', top: -46, right: -34,
              width: 150, height: 150, borderRadius: '50%',
              background: alpha(theme.palette.primary.main, 0.22),
              filter: 'blur(40px)', pointerEvents: 'none',
            }} />
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative' }}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Box
                  component="img"
                  src="/logo_icon.webp"
                  alt="Bilalsadasub"
                  sx={{
                    width: 44, height: 44, borderRadius: '50%',
                    objectFit: 'cover', bgcolor: '#000',
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                  }}
                />
                <Box sx={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 12, height: 12, borderRadius: '50%',
                  bgcolor: '#22c55e', border: '2px solid #131313',
                  animation: `${pulseRing} 2s ease-in-out infinite`,
                }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', lineHeight: 1.15 }}>
                  Bilalsadasub Support
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.25 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
                    Online • replies in minutes
                  </Typography>
                </Stack>
              </Box>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: '#fff', bgcolor: alpha('#fff', 0.08), '&:hover': { bgcolor: alpha('#fff', 0.16) } }}
              >
                <Iconify icon="eva:close-fill" width={18} />
              </IconButton>
            </Stack>
          </Box>

          {/* Messages */}
          <Box
            ref={listRef}
            onScroll={handleListScroll}
            sx={{
              flex: 1, minHeight: 0, overflowY: 'auto',
              p: 1.5,
              bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.02 : 0.06),
            }}
          >
            {/* Loading-older spinner at the top — scroll up to fetch the
                next page of history. Disappears once the API returns an
                empty page (`olderExhaustedRef`). */}
            {loadingOlder && (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 1.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Loading older messages…
                </Typography>
              </Stack>
            )}
            {/* Shimmer while we haven't completed a server round-trip yet.
                Avoids flashing the "Say hi" empty state for returning users
                with existing history. */}
            {!loaded && messages.length === 0 && !error && <MessageShimmer />}

            {loaded && messages.length === 0 && !error && (
              <Stack alignItems="center" sx={{ py: 6 }} spacing={1}>
                <Iconify icon="eva:message-square-outline" width={36} sx={{ color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 240 }}>
                  Say hi to start a conversation. Our team will reply right here.
                </Typography>
              </Stack>
            )}

            {error && (
              <Box sx={{
                p: 1.5, borderRadius: 1.5,
                bgcolor: alpha('#ef4444', 0.08),
                border: `1px solid ${alpha('#ef4444', 0.3)}`,
                display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5,
              }}>
                <Iconify icon="eva:alert-circle-fill" width={18} sx={{ color: '#b91c1c', mt: '2px' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 700 }}>
                    {error}
                  </Typography>
                  <Button size="small" onClick={bootstrap} sx={{ fontWeight: 700, fontSize: '0.7rem', mt: 0.5 }}>
                    Retry
                  </Button>
                </Box>
              </Box>
            )}

            <Stack spacing={1}>
              {messages.map((m, i) => {
                const prev = messages[i - 1];
                const showAvatar = !m.fromUser && (!prev || prev.fromUser || prev.senderName !== m.senderName);
                return <Bubble key={m.id} msg={m} showAvatar={showAvatar} />;
              })}
            </Stack>
          </Box>

          {/* Input */}
          <Box component="form" onSubmit={submit} sx={{
            p: 1.25,
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: 'background.paper',
          }}>
            {/* Hidden file picker — triggered by the paperclip button. */}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={onFileChosen}
              accept="image/*,application/pdf,text/plain,application/zip,.doc,.docx,.xls,.xlsx,.csv"
            />

            {/* Pending attachment chip — shows above the input until sent. */}
            {attachment && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1, py: 0.75, mb: 1,
                  borderRadius: 2,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                }}
              >
                {attachmentPreview ? (
                  <Box component="img" src={attachmentPreview} alt={attachment.name} sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1, flexShrink: 0,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    color: 'primary.main',
                    display: 'grid', placeItems: 'center',
                  }}>
                    <Iconify icon="eva:file-text-fill" width={18} />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {attachment.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {humanFileSize(attachment.size)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={clearAttachment} aria-label="Remove attachment">
                  <Iconify icon="eva:close-fill" width={16} />
                </IconButton>
              </Stack>
            )}

            <TextField
              fullWidth
              size="small"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={attachment ? 'Add a caption…' : 'Type a message…'}
              disabled={!conversationId}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={pickFile}
                      disabled={!conversationId}
                      size="small"
                      aria-label="Attach a file"
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <Iconify icon="eva:attach-2-outline" width={18} />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="submit"
                      disabled={(!draft.trim() && !attachment) || !conversationId}
                      size="small"
                      sx={{ color: 'primary.main' }}
                    >
                      <Iconify icon="eva:paper-plane-fill" width={18} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
            />
          </Box>
        </Panel>
      )}
    </>
  );
}

// ---------------------------------------------------------------------

function bubbleBgColor(t, mine) {
  if (mine) return t.palette.primary.main;
  return t.palette.mode === 'light' ? '#fff' : alpha(t.palette.text.primary, 0.06);
}

// Shimmer skeleton — 4 fake bubbles alternating left/right so the load
// state looks like a real conversation rendering in.
const SHIMMER_BUBBLES = [
  { mine: false, w: 180 },
  { mine: true,  w: 140 },
  { mine: false, w: 220 },
  { mine: true,  w: 100 },
];

function MessageShimmer() {
  return (
    <Stack spacing={1} sx={{ py: 0.5 }}>
      {SHIMMER_BUBBLES.map((b, i) => (
        <Stack
          key={i}
          direction="row"
          spacing={1}
          alignItems="flex-end"
          justifyContent={b.mine ? 'flex-end' : 'flex-start'}
        >
          {!b.mine && <Skeleton variant="circular" width={26} height={26} sx={{ flexShrink: 0 }} />}
          <Skeleton
            variant="rounded"
            width={b.w}
            height={36}
            sx={{
              borderRadius: b.mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.06 : 0.12),
            }}
          />
        </Stack>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------
//  WhatsApp-style inline formatting:
//    *bold*  _italic_  ~strike~  `inline code`  ```block code```
//  URLs auto-link. The block scanner is greedy on triple-backtick fences
//  so multiline code survives newlines.
// ---------------------------------------------------------------------

const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;

// Parse a single line into an array of React nodes, applying inline
// formatting markers. Markers must sit at word boundaries — exactly
// what WhatsApp does — so phrases like "5*2" or "snake_case_name"
// don't accidentally get styled.
function renderInline(line, keyPrefix) {
  // Pull URLs out first so the inline scanner doesn't chew on them.
  const out = [];
  let lastIdx = 0;
  let segNum = 0;
  const pushStyled = (text, from) => {
    if (!text) return;
    let cursor = 0;
    const tokens = [];
    // Iterate: at each step find the EARLIEST formatting match.
    while (cursor < text.length) {
      const slice = text.slice(cursor);
      const m =
        slice.match(/(^|[\s({[<])`([^`\n]+)`(?=$|[\s,.;:!?)\]}>])/) ||
        slice.match(/(^|[\s({[<])\*([^*\n]+)\*(?=$|[\s,.;:!?)\]}>])/) ||
        slice.match(/(^|[\s({[<])_([^_\n]+)_(?=$|[\s,.;:!?)\]}>])/) ||
        slice.match(/(^|[\s({[<])~([^~\n]+)~(?=$|[\s,.;:!?)\]}>])/);
      if (!m) {
        tokens.push(slice);
        break;
      }
      const startInSlice = m.index + m[1].length;
      const before = slice.slice(0, startInSlice);
      if (before) tokens.push(before);
      const inner = m[2];
      const marker = slice[startInSlice];
      const key = `${keyPrefix}-${from}-${segNum}`;
      segNum += 1; // eslint-disable-line no-plusplus
      if (marker === '`') {
        tokens.push(
          <Box
            key={key}
            component="code"
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.82em',
              px: 0.5, py: 0.1,
              borderRadius: 0.75,
              bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.08 : 0.18),
            }}
          >
            {inner}
          </Box>
        );
      } else if (marker === '*') {
        tokens.push(<Box key={key} component="strong" sx={{ fontWeight: 800 }}>{inner}</Box>);
      } else if (marker === '_') {
        tokens.push(<Box key={key} component="em" sx={{ fontStyle: 'italic' }}>{inner}</Box>);
      } else if (marker === '~') {
        tokens.push(<Box key={key} component="s" sx={{ textDecoration: 'line-through' }}>{inner}</Box>);
      }
      cursor += startInSlice + inner.length + 2; // marker + inner + marker
    }
    out.push(<span key={`${keyPrefix}-${from}-w`}>{tokens}</span>);
  };

  line.replace(URL_REGEX, (match, _g1, offset) => {
    if (offset > lastIdx) pushStyled(line.slice(lastIdx, offset), offset);
    const href = match.startsWith('www.') ? `https://${match}` : match;
    out.push(
      <Box
        key={`${keyPrefix}-u-${offset}`}
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600, wordBreak: 'break-all' }}
      >
        {match}
      </Box>
    );
    lastIdx = offset + match.length;
    return match;
  });
  if (lastIdx < line.length) pushStyled(line.slice(lastIdx), lastIdx);
  return out.length ? out : line;
}

// Whole-message renderer. Extracts triple-backtick code blocks first
// (they may span newlines), then runs the inline scanner per line and
// keeps line breaks visible.
function renderRichText(text) {
  if (!text) return null;
  const out = [];
  const re = /```([\s\S]+?)```/g;
  let last = 0;
  let m;
  let n = 0;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      const k = `t${n}`; n += 1;
      out.push(...renderLines(text.slice(last, m.index), k));
    }
    const cbKey = `cb-${n}`; n += 1;
    out.push(
      <Box
        key={cbKey}
        component="pre"
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.78rem',
          m: 0, my: 0.5, p: 1,
          borderRadius: 1.25,
          bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.08 : 0.18),
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {m[1].replace(/^\n|\n$/g, '')}
      </Box>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) { const tailKey = `t${n}`; n += 1; out.push(...renderLines(text.slice(last), tailKey)); }
  return out;
}

function renderLines(chunk, keyPrefix) {
  const lines = chunk.split('\n');
  return lines.map((line, idx) => (
    <span key={`${keyPrefix}-l${idx}`}>
      {renderInline(line, `${keyPrefix}-l${idx}`)}
      {idx < lines.length - 1 ? <br /> : null}
    </span>
  ));
}

function AttachmentView({ attachment, mine }) {
  const { url, thumb, fileType, name, size } = attachment;
  const isImg = isImageMime(fileType) || /\.(png|jpe?g|gif|webp|svg)$/i.test(name || '');
  if (isImg && (thumb || url)) {
    return (
      <Box
        component="a"
        href={url || thumb}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'block',
          maxWidth: 260,
          borderRadius: 2,
          overflow: 'hidden',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={thumb || url}
          alt={name || 'image'}
          sx={{ display: 'block', width: '100%', maxHeight: 220, objectFit: 'cover' }}
        />
      </Box>
    );
  }
  // Non-image: file chip with download link.
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1, py: 0.85,
        borderRadius: 1.5,
        textDecoration: 'none',
        color: mine ? 'inherit' : 'text.primary',
        bgcolor: (t) => (mine ? alpha('#fff', 0.16) : alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.04 : 0.12)),
        border: (t) => `1px solid ${mine ? alpha('#fff', 0.18) : t.palette.divider}`,
        maxWidth: 260,
      }}
    >
      <Box sx={{ width: 28, height: 28, borderRadius: 0.75, display: 'grid', placeItems: 'center', bgcolor: (t) => (mine ? alpha('#fff', 0.18) : alpha(t.palette.primary.main, 0.14)), color: mine ? '#fff' : 'primary.main', flexShrink: 0 }}>
        <Iconify icon="eva:file-text-fill" width={16} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name || 'attachment'}
        </Typography>
        <Typography variant="caption" sx={{ color: mine ? alpha('#fff', 0.75) : 'text.secondary' }}>
          {size ? humanFileSize(size) : 'Download'}
        </Typography>
      </Box>
      <Iconify icon="eva:download-outline" width={14} sx={{ flexShrink: 0, opacity: 0.8 }} />
    </Box>
  );
}

function Bubble({ msg, showAvatar }) {
  const mine = msg.fromUser;
  const time = msg.createdAt instanceof Date
    ? msg.createdAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
    : '';
  return (
    <Stack direction="row" spacing={1} alignItems="flex-end" justifyContent={mine ? 'flex-end' : 'flex-start'}>
      {!mine && showAvatar ? (
        <Avatar
          src={msg.senderAvatar || undefined}
          alt={msg.senderName || 'Agent'}
          sx={{ width: 26, height: 26, fontSize: '0.7rem', fontWeight: 800 }}
        >
          {(msg.senderName || 'A').charAt(0).toUpperCase()}
        </Avatar>
      ) : !mine && <Box sx={{ width: 26 }} />}

      <Box sx={{
        maxWidth: '78%',
        px: 1.5, py: 1,
        borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        background: (t) => (mine
          ? `linear-gradient(135deg, ${t.palette.primary.light} 0%, ${t.palette.primary.main} 100%)`
          : bubbleBgColor(t, mine)),
        color: mine ? 'primary.contrastText' : 'text.primary',
        border: (t) => (mine ? 'none' : `1px solid ${t.palette.divider}`),
        boxShadow: mine
          ? (t) => `0 6px 16px -8px ${alpha(t.palette.primary.main, 0.7)}`
          : '0 1px 2px rgba(0,0,0,0.04)',
        opacity: msg.sending ? 0.65 : 1,
      }}>
        {!mine && showAvatar && msg.senderName && (
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: 'text.secondary', mb: 0.25 }}>
            {msg.senderName}
          </Typography>
        )}
        {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
          <Stack spacing={0.75} sx={{ mb: msg.content ? 0.75 : 0 }}>
            {msg.attachments.map((a) => (
              <AttachmentView key={a.id} attachment={a} mine={mine} />
            ))}
          </Stack>
        )}
        {msg.content && (
          <Typography component="div" sx={{ fontSize: '0.85rem', wordBreak: 'break-word', lineHeight: 1.45 }}>
            {renderRichText(msg.content)}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.25 }}>
          {msg.failed && <Iconify icon="eva:alert-circle-fill" width={12} sx={{ color: '#ef4444' }} />}
          {msg.sending && <Iconify icon="eva:clock-outline" width={12} sx={{ opacity: 0.7 }} />}
          <Typography variant="caption" sx={{
            fontSize: '0.62rem',
            color: mine ? 'rgba(19,19,19,0.55)' : 'text.disabled',
            fontWeight: 600,
          }}>
            {time}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
